import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import MyCaptcha from '../FormFromApiData/ReCaptcha';

interface IContactUsData {
  Disabled?: boolean;
  RedirectTo?: string;
  ContactHtml?: string;
  DisplayForm?: boolean;
  ReCaptchaRequired?: boolean;
  PrefillName?: string;
  PrefillEmail?: string;
  DisplayContactPersonName?: boolean;
  DisplayContactPersonPhone?: boolean;
  DisplayContactPersonEmail?: boolean;
  ContactPersonName?: string;
  ContactPersonPhone?: string;
  ContactPersonEmail?: string;
  Subjects?: string[];
}

/**
 * Replaces contactUs.ascx on Default.aspx.
 */
const ContactUsPage = () => {
  const [data, setData] = useState<IContactUsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('General Inquiry');
  const [otherSubject, setOtherSubject] = useState('');
  const [message, setMessage] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    store.server
      .postApi<ServerResponse<IContactUsData>>('../ContactUs/Data', {})
      .then((x) => {
        if (x.Success && x.Value) {
          if (x.Value.RedirectTo) {
            window.location.href = x.Value.RedirectTo;
            return;
          }
          setData(x.Value);
          if (x.Value.PrefillName) setName(x.Value.PrefillName);
          if (x.Value.PrefillEmail) setEmail(x.Value.PrefillEmail);
          if (x.Value.Subjects?.length) setSubject(x.Value.Subjects[0]);
        } else {
          toast.error(x.Message || 'Unable to load contact page.', { autoClose: false });
        }
      })
      .catch(() => toast.error('Unable to load contact page.', { autoClose: false }))
      .finally(() => setLoading(false));
  }, []);

  const send = () => {
    toast.dismiss();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Name, email, and message are required.');
      return;
    }
    let captchaToken = captcha;
    if (!captchaToken) {
      const w: any = window;
      captchaToken = w.x_reCaptcha || '';
    }
    setSending(true);
    store.server
      .postApi<ServerResponse<{ Sent?: boolean }>>('../ContactUs/Send', {
        name,
        email,
        phone,
        subject,
        otherSubject,
        message,
        captcha: captchaToken,
      })
      .then((x) => {
        if (x.Success) {
          setSent(true);
        } else {
          toast.error(x.Message || 'Unable to send message.', { autoClose: false });
        }
      })
      .catch(() => toast.error('Unable to send message.', { autoClose: false }))
      .finally(() => setSending(false));
  };

  if (loading || !data) {
    return (
      <div className="main-body-page grow" data-testid="contact-us-page" style={{ minHeight: 120 }}>
        <p>Loading…</p>
      </div>
    );
  }

  const showDirect =
    data.DisplayContactPersonName || data.DisplayContactPersonPhone || data.DisplayContactPersonEmail;

  return (
    <div className="main-body-page grow" data-testid="contact-us-page" style={{ minHeight: 120 }}>
      <h2>Contact Us</h2>
      {data.ContactHtml && (
        <div
          className="editable"
          data-testid="contact-us-html"
          dangerouslySetInnerHTML={{ __html: data.ContactHtml }}
        />
      )}

      {sent && (
        <div className="alert alert-success" data-testid="contact-us-sent">
          Your message has been sent. We will get back to you as soon as possible.
          <strong> Thank you.</strong>
        </div>
      )}

      {!sent && data.DisplayForm && (
        <div data-testid="contact-us-form">
          <h3>Contact Us Form</h3>
          <div className="form-horizontal">
            <div className="form-group">
              <label className="control-label col-md-4" htmlFor="cu-name">
                Name:
              </label>
              <div className="col-md-8">
                <input
                  id="cu-name"
                  className="form-control"
                  data-testid="contact-us-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-md-4" htmlFor="cu-email">
                E-Mail:
              </label>
              <div className="col-md-8">
                <input
                  id="cu-email"
                  type="email"
                  className="form-control"
                  data-testid="contact-us-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-md-4" htmlFor="cu-phone">
                Phone:
              </label>
              <div className="col-md-8">
                <input
                  id="cu-phone"
                  className="form-control"
                  data-testid="contact-us-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-md-4" htmlFor="cu-subject">
                Subject:
              </label>
              <div className="col-md-8">
                <select
                  id="cu-subject"
                  className="form-control"
                  data-testid="contact-us-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                >
                  {(data.Subjects || []).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {subject === 'Other' && (
                  <div className="head-room" data-testid="contact-us-other-subject-wrap">
                    <label htmlFor="cu-other-subject">Enter Subject:</label>
                    <input
                      id="cu-other-subject"
                      className="form-control"
                      data-testid="contact-us-other-subject"
                      value={otherSubject}
                      onChange={(e) => setOtherSubject(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-md-4" htmlFor="cu-message">
                Message:
              </label>
              <div className="col-md-8">
                <textarea
                  id="cu-message"
                  className="form-control"
                  data-testid="contact-us-message"
                  rows={6}
                  maxLength={8000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="How can we help?"
                />
              </div>
            </div>
            {data.ReCaptchaRequired && (
              <div className="form-group head-room" data-testid="contact-us-captcha">
                <div className="col-md-8 col-md-offset-4">
                  <MyCaptcha
                    fieldChanged={(_key: string, v: string[]) => {
                      setCaptcha(v?.[0] || '');
                    }}
                  />
                </div>
              </div>
            )}
            <div className="form-group">
              <div className="col-md-8 col-md-offset-4">
                <button
                  type="button"
                  className="btn btn-primary"
                  data-testid="contact-us-send"
                  onClick={send}
                  disabled={sending}
                >
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!sent && showDirect && (
        <div data-testid="contact-us-direct">
          <h3>Direct Contact Information</h3>
          <div className="form-horizontal">
            {data.DisplayContactPersonName && (
              <div className="form-group">
                <label className="control-label col-md-4">Name:</label>
                <div className="col-md-8 form-control-static" data-testid="contact-us-direct-name">
                  {data.ContactPersonName}
                </div>
              </div>
            )}
            {data.DisplayContactPersonPhone && (
              <div className="form-group">
                <label className="control-label col-md-4">Phone:</label>
                <div className="col-md-8 form-control-static" data-testid="contact-us-direct-phone">
                  {data.ContactPersonPhone}
                </div>
              </div>
            )}
            {data.DisplayContactPersonEmail && (
              <div className="form-group">
                <label className="control-label col-md-4">E-Mail:</label>
                <div className="col-md-8 form-control-static" data-testid="contact-us-direct-email">
                  {data.ContactPersonEmail}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactUsPage;
